import { formatDate } from "@data/utility/Utils"
import moment from "moment"
import { theme } from "../../common/theme/theme"
import { findClosestNextOccurrence } from "../../student/account/onboarding/LiveRoomCard"

export const subscriptionPausedStatuses = [
    "on_grace_period_before_pause",
    "paused",
    "planned",
]

/**
 * Les statuts `subscription_state.status` qui signifient « cette famille est
 * abonnée et paie ». Source : `StudentTrait::getSubscriptionState()` côté back.
 *
 * Liste nommée (et non une chaîne de `||`) pour la même raison que
 * `subscriptionPausedStatuses` : `isSubscribed` garde ~12 points d'appel qui
 * décident tous d'après elle (bouton « Résilier », date de prélèvement, achat
 * d'heure supplémentaire, upsells « S'abonner »). Un statut oublié ici ne casse
 * rien de visible — il déclasse silencieusement un abonné en prospect.
 *
 * `planned_subscription_update` est le cas qui a coûté P-2713 : c'est une
 * ANNOTATION (« un changement d'offre est programmé », typiquement la reprise de
 * rentrée écrite par le cockpit d'été), mais `getSubscriptionState()` la renvoie
 * comme un statut exclusif, court-circuité AVANT la branche `recurring`. Un
 * abonnement Stripe `active`, carte en banque et engagement en cours se
 * présentait donc comme non-abonné, et le parent perdait l'entrée de
 * résiliation.
 */
export const subscribedStatuses = [
    "recurring",
    "on_trial",
    "on_grace_period",
    "on_grace_period_before_pause",
    "planned",
    "past_due",
    "planned_subscription_update",
]

export const findItemInListWithId = (id, list, otherFieldName) => {
    return list?.find((item) => {
        return item?.id === parseInt(id) || item?.[otherFieldName] === id
    })
}

export const idsToObjects = (idList, objectList) => {
    return idList?.map((x) => objectList?.find((y) => y.id === x))
}

export const tasksForActivities = (activities) => {
    let tasks = []

    activities?.forEach((activity) => {
        tasks = tasks.concat(activity?.tasks)
    })
    return tasks
}

export const filterTodayActivitiesForChallenge = (allActivities, challenge) => {
    const today = moment().format("YYYY-MM-DD")

    const challengeActivities = idsToObjects(
        challenge.activity_ids,
        allActivities
    )
    return challengeActivities?.filter((activity) =>
        moment(activity?.date).isSame(today, "day")
    )
}

export const countLateActivitiesForChallenge = (allActivities, challenge) => {
    const challengeActivities = idsToObjects(
        challenge.activity_ids,
        allActivities
    )
    return challengeActivities?.filter((activity) => activity?.is_late)?.length
}

export const interviewsForStudents = (students) => {
    let interviews = []

    students?.forEach((student) => {
        interviews = interviews.concat(student?.interviews)
    })

    return interviews.sort((a, b) => moment(a.date_time).diff(b.date_time))
}

export const studentActivitiesForChallengeId = (student, challengeId) => {
    return student?.activities?.filter((x) => x.challenge_id === challengeId)
}

export const studentActivitiesForActivityTemplateId = (
    student,
    activityTemplateId
) => {
    return student?.activities?.filter(
        (x) => x?.activity_template_id === activityTemplateId
    )
}

export const studentSurveyForActivityTemplate = (student, activityTemplate) => {
    return student?.survey_answers?.filter(
        (x) => x.id === activityTemplate.student_survey_question_id
    )
}

export const nextInterviewForStudent = (student) => {
    const nextOrTwoDaysBeforeList = student?.interviews
        ?.filter(
            (x) =>
                !x.finalized_at &&
                moment(x?.date_time).isAfter(moment().subtract(2, "day"))
        )
        ?.sort((a, b) => moment(a?.date_time)?.diff(b?.date_time))

    if (nextOrTwoDaysBeforeList?.length > 0) {
        return nextOrTwoDaysBeforeList[0]
    }

    return null
}

export const todayLivesForStudent = (student) => {
    const sameDayList = student?.live_sessions
        ?.filter((x) => moment(x?.day_time).isSame(moment(), "day"))
        ?.sort((a, b) => moment(a?.day_time)?.diff(b?.day_time))

    if (sameDayList?.length > 0) {
        return sameDayList
    }

    return null
}

export const lastFinishedInterviewForStudent = (student) => {
    const finishedInterviews = student?.interviews
        ?.filter((x) => !!x?.finalized_at)
        ?.sort((a, b) => moment(b.date_time).diff(a.date_time))

    if (finishedInterviews?.length > 0) {
        return finishedInterviews[0]
    }

    return null
}

export const isWebConfVisibleForStudent = (student) => {
    const interview = nextInterviewForStudent(student)
    if (!interview) return false

    return moment(interview?.date_time).isSameOrBefore(moment(), "day")
}

/**
 * Gets the most relevant activity based on:
 * 1. Most recent locked_at (if any exist)
 * 2. If no locked_at, most recent started_at
 * 3. If no started_at, most recent created_at
 *
 * @param {Array} activities - Array of activity objects
 * @param {String} category - The category of the activity to filter by
 * @returns {Object} The selected activity
 */
export function getMostRecentActivity(activities, category = "write") {
    let filteredActivities = []

    if (!category) {
        filteredActivities = activities
    } else {
        filteredActivities = activities?.filter(
            (activity) => activity?.category === category
        )
    }

    if (!filteredActivities?.length) return null

    const ongoingActivity = filteredActivities.reduce((latest, current) => {
        if (!current?.finished_at) {
            if (
                !latest ||
                new Date(current?.started_at) > new Date(latest?.started_at)
            ) {
                return current
            }
        }
        return latest
    }, null)

    if (ongoingActivity) return ongoingActivity

    const startedActivity = filteredActivities.reduce((latest, current) => {
        if (current.started_at) {
            if (
                !latest ||
                new Date(current.started_at) > new Date(latest.started_at)
            ) {
                return current
            }
        }
        return latest
    }, null)

    if (startedActivity) return startedActivity

    return filteredActivities.reduce((latest, current) => {
        if (
            !latest ||
            new Date(current?.created_at) > new Date(latest?.created_at)
        ) {
            return current
        }
        return latest
    }, null)
}

export const getActivityStatus = (activity) => {
    if (activity === null) {
        return "none"
    }

    if (activity?.finished_at) {
        return "finished"
    }

    return "ongoing"
}

const getActivityRealizationItemStatus = (
    activity,
    currentActivityId = null
) => {
    if (activity?.finished_at) {
        return "finished"
    }

    if (Number(currentActivityId) === Number(activity?.id)) {
        return "ongoing"
    }

    return "not_started"
}

export const getActivityRealizationItemColor = (
    activity,
    currentActivityId = null
) => {
    const status = getActivityRealizationItemStatus(activity, currentActivityId)

    switch (status) {
        case "finished":
            return theme.colors.success
        case "ongoing":
            return theme.colors.primary
        case "not_started":
            return theme.colors.grey
        default:
            return theme.colors.grey
    }
}

export const isLiveVisibleForStudent = (student) => {
    const live = nextLiveForStudent(student)
    if (!live) return false

    return moment(interview?.date_time).isSameOrBefore(moment(), "day")
}

export const activitiesForStudent = (student) => {
    return student.activities?.tasks?.filter((x) =>
        moment(x?.date).isSame(moment(), "day")
    )
}

export const isSubscribed = (student) =>
    subscribedStatuses.includes(student?.subscription_state?.status)
export const countSameActivitiesToday = (
    activityTemplate,
    activities,
    challenge_id = null,
    school_subject_id = null
) => {
    if (activityTemplate?.parent_type === "challenge") {
        return activities
            ?.filter((x) => x?.activity_template_id === activityTemplate?.id)
            ?.filter((x) => x?.challenge_id === challenge_id)
            ?.filter(
                (x) => x?.finished_at && moment().isSame(x?.finished_at, "day")
            )
            ?.filter((x) => x?.status !== "rejected")?.length
    } else {
        return activities
            ?.filter((x) => x?.activity_template_id === activityTemplate?.id)
            ?.filter((x) => x?.school_subject_id === school_subject_id)
            ?.filter(
                (x) => x?.finished_at && moment().isSame(x?.finished_at, "day")
            )
            ?.filter((x) => x?.status !== "rejected")?.length
    }
}

export const countSameAgainActivities = (
    activityTemplate,
    activities,
    againActivityId
) => {
    const activity = activities?.find((x) => x?.id === againActivityId)

    if (!activity || !activity?.finished_at) return 0

    return (
        1 +
        activities
            ?.filter((x) => x?.activity_template_id === activityTemplate?.id)
            ?.filter((x) => x?.again_activity_id === againActivityId)
            ?.filter((x) => !!x?.finished_at)
            ?.filter((x) => x?.status !== "rejected")?.length
    )
}

export const todaySchoolSubjectActivities = (user, activityTemplates) => {
    let doneCount = 0
    let totalCount = 0

    const todaySchoolSubjects = user?.school_subjects?.filter(
        (s) => s?.prioritized
    )

    const todaySchoolSubjectActivities = []

    const todayWeekDay = moment().format("dddd")

    todaySchoolSubjects?.forEach((ts) => {
        const titles = {
            lesson_day:
                "Le " + todayWeekDay + " tu as cours de " + ts?.name + "...",
            lesson_day_before:
                "Prépare-toi pour ton cours de " + ts?.name + " de demain...",
            lesson_day_after: "Hier, tu as eu cours de " + ts?.name + "...",
            week_end:
                "On est le week-end, profite-en pour t'avancer en " + ts?.name,
        }

        let filteredATs = []

        Object.keys(titles)?.forEach((t) => {
            if (ts?.today_status[t]) {
                filteredATs = filteredATs?.concat(
                    activityTemplates?.filter((at) => at?.schedule_day === t)
                )
            }
        })

        if (filteredATs?.length > 0) {
            const filteredActivities = user?.activities
                ?.filter((a) =>
                    filteredATs
                        ?.map((at) => at?.id)
                        ?.includes(a?.activity_template_id)
                )
                ?.filter((a) => a?.school_subject_id === ts?.id)
                ?.filter((a) => moment().isSame(a?.finished_at, "day"))
            // ?.filter(a => !!a?.finished_at)

            // console.log(filteredActivities)

            const doneFilteredATs = filteredATs?.filter((at) =>
                filteredActivities
                    ?.map((a) => a?.activity_template_id)
                    ?.includes(at?.id)
            )

            doneCount += doneFilteredATs?.length
            totalCount += filteredATs?.length

            filteredATs?.forEach((at) => {
                todaySchoolSubjectActivities.push({
                    // title: titles[t],
                    school_subject: ts,
                    activity_template: at,
                    activities: filteredActivities?.filter(
                        (a) => a?.activity_template_id === at?.id
                    ),
                })
            })

            // todaySchoolSubjectActivities.push({
            //     // title: titles[t],
            //     school_subject: ts,
            //     activity_templates: filteredATs,
            //     activities: filteredActivities
            // })
        }
        // else {
        //     todaySchoolSubjectActivities.push({
        //         // title: titles[t],
        //         school_subject: ts,
        //         activity_templates: [],
        //         activities: []
        //     })
        // }
    })

    return {
        items: todaySchoolSubjectActivities,
        total_count: totalCount,
        done_count: doneCount,
    }
}

export const todayChallengeActivities = (user, activityTemplates) => {
    let doneCount = 0
    let totalCount = 0
    const todayChallengeActivities = []

    user?.challenges
        ?.filter((c) => {
            if (!c?.planning_mode) return false

            const todayActivities = user?.activities?.filter(
                (a) => a?.challenge_id === c?.id && a?.is_shown_today
            )
            return todayActivities?.length > 0
        })
        ?.forEach((c) => {
            const todayActivities = user?.activities?.filter(
                (a) => a?.challenge_id === c?.id && a?.is_shown_today
            )
            const againActivities = user?.activities?.filter(
                (a) => a?.challenge_id === c?.id && a?.again_activity_id
            )
            const doneTodayActivities = todayActivities?.filter(
                (a) => !!a?.finished_at
            )
            totalCount += todayActivities?.length
            doneCount += doneTodayActivities?.length

            todayActivities?.forEach((ta) => {
                todayChallengeActivities.push({
                    challenge: c,
                    activities: todayActivities
                        ?.filter(
                            (a) =>
                                a?.activity_template_id ===
                                ta?.activity_template_id
                        )
                        ?.concat(
                            againActivities?.filter(
                                (a) =>
                                    a?.activity_template_id ===
                                    ta?.activity_template_id
                            )
                        ),
                    activityPlanned: ta,
                    activity_template: activityTemplates?.find(
                        (at) => at?.id === ta?.activity_template_id
                    ),
                })
            })
        })

    return {
        items: todayChallengeActivities,
        total_count: totalCount,
        done_count: doneCount,
    }
}

export const sortByDateAttribute = (
    list,
    dateAttribute = "date",
    asc = true
) => {
    return list?.sort(
        (a, b) =>
            moment(a?.[dateAttribute]).diff(b?.[dateAttribute]) * (asc ? 1 : -1)
    )
}

export const groupBy = (xs, key) => {
    return xs?.reduce(function (rv, x) {
        ;(rv[x[key]] = rv[x[key]] || []).push(x)
        return rv
    }, {})
}

export const isSameCalendarDay = (moment1, moment2) => {
    return moment(moment1?.format("YYYY-MM-DD"))?.isSame(
        moment2?.format("YYYY-MM-DD")
    )
}

export const sortLiveSessionsCallback = (a, b) => {
    const closestNextOccurrenceA = findClosestNextOccurrence([a]) // 2023-10-02
    const closestNextOccurrenceB = findClosestNextOccurrence([b]) // 2023-09-28

    const liveMomentA = moment(closestNextOccurrenceA?.date)
    const liveMomentB = moment(closestNextOccurrenceB?.date)

    const diffA = isSameCalendarDay(liveMomentA, moment())
        ? Math.abs(liveMomentA.diff(moment(), "minutes"))
        : liveMomentA.diff(moment(), "minutes")
    const diffB = isSameCalendarDay(liveMomentB, moment())
        ? Math.abs(liveMomentB.diff(moment(), "minutes"))
        : liveMomentB.diff(moment(), "minutes")

    return diffB > diffA ? -1 : diffB < diffA ? 1 : 0
}

export const isSubscriptionPaused = (user) =>
    subscriptionPausedStatuses.includes(user?.subscription_state?.status)
export const isSubscriptionRecurring = (user) =>
    user?.subscription_state?.status === "recurring" ||
    user?.subscription_state?.status === "past_due" ||
    user?.subscription_state?.status === "on_grace_period"
export const isSubscriptionTrialing = (user) =>
    user?.subscription_state?.status === "on_trial"

export const subscriptionPausePeriod = (user) =>
    moment(user?.subscription_state?.end_date).isBefore(
        user?.subscription_state?.planned_start_date
    )
        ? `du ${formatDate(user?.subscription_state?.end_date)} au ${formatDate(
              moment(user?.subscription_state?.planned_start_date)
          )}`
        : ""

export const isPausePlanned = (user) =>
    user?.subscription_state?.status === "on_grace_period_before_pause"

export const isPauseActive = (user) =>
    user?.subscription_state?.status === "paused"

export const getTodaysFinishedActivities = (user) => {
    const finishedToday = user?.activities
        ?.filter((activity) =>
            moment(activity.finished_at).isSame(moment(), "day")
        )
        ?.sort((a, b) => moment(b.finished_at).diff(moment(a.finished_at)))

    const last3BeforeToday = user?.activities
        ?.filter(
            (activity) => !moment(activity.finished_at).isSame(moment(), "day")
        )
        ?.sort((a, b) => moment(b.finished_at).diff(moment(a.finished_at)))
        ?.slice(0, 2)

    return finishedToday?.concat(last3BeforeToday)?.slice(0, 5) || []
}

export const getSubjectTodoActivities = (activity, user, noCurrent = false) => {
    const activitySubjectId = activity?.material?.school_subject_id ?? null
    const todoActivities =
        user?.todo_activities?.filter(
            (todo) =>
                !activitySubjectId ||
                todo?.material?.school_subject_id === activitySubjectId
        ) || []

    if (noCurrent) {
        return todoActivities?.filter((todo) => !todo?.current)
    }

    return todoActivities
}

export const checkIfNoActivity = (user) => {
    return user?.todo_activities?.length === 0
}

export const numberToWords = (number) => {
    return [
        "un",
        "deux",
        "trois",
        "quatre",
        "cinq",
        "six",
        "sept",
        "huit",
        "neuf",
        "dix",
    ][number - 1]
}

export const removeDuplicateGroups = (groups) => {
    const uniqueGroups = []
    const seenGroupIds = new Set()

    for (const group of groups) {
        // Generate unique ID based on sessions
        const sessionIds = group.live_sessions.map((session, index) => {
            if (session.id != null) {
                return String(session.id)
            }
            // For sessions without ID, use day_time and tutor info and index
            const tutorId =
                session.tutor?.id || session.liveRoom?.tutor?.id || "unknown"
            return `adhoc_${session.day_time}_${tutorId}_${index}`
        })

        // Sort to normalize order
        sessionIds.sort()
        const groupId = sessionIds.join("|")

        if (!seenGroupIds.has(groupId)) {
            seenGroupIds.add(groupId)
            uniqueGroups.push(group)
        } else {
            // If we've seen this group before, merge tags
            const existingGroup = uniqueGroups.find((existing) => {
                const existingSessionIds = existing.live_sessions.map(
                    (session, index) => {
                        if (session.id != null) {
                            return String(session.id)
                        }
                        const tutorId =
                            session.tutor?.id ||
                            session.liveRoom?.tutor?.id ||
                            "unknown"
                        return `adhoc_${session.day_time}_${tutorId}_${index}`
                    }
                )
                existingSessionIds.sort()
                return existingSessionIds.join("|") === groupId
            })

            if (existingGroup) {
                // Merge tags and keep unique ones
                const mergedTags = [
                    ...new Set([
                        ...(existingGroup.tags || []),
                        ...(group.tags || []),
                    ]),
                ]
                existingGroup.tags = mergedTags
            }
        }
    }

    return uniqueGroups
}

export const sortGroupsByPriority = (groups) => {
    return groups.sort((a, b) => {
        const aTags = a.tags || []
        const bTags = b.tags || []

        // Priority 1: different_days
        const aHasDifferentDays = aTags.includes("different_days")
        const bHasDifferentDays = bTags.includes("different_days")

        if (aHasDifferentDays && !bHasDifferentDays) {
            return -1
        }
        if (!aHasDifferentDays && bHasDifferentDays) {
            return 1
        }

        // If both have same different_days status, check Priority 2: exact_slot
        if (aHasDifferentDays === bHasDifferentDays) {
            const aHasExactSlot = aTags.includes("exact_slot")
            const bHasExactSlot = bTags.includes("exact_slot")

            if (aHasExactSlot && !bHasExactSlot) {
                return -1
            }
            if (!aHasExactSlot && bHasExactSlot) {
                return 1
            }

            // If both have same exact_slot status, check Priority 3: single_tutor with ad-hoc handling
            if (aHasExactSlot === bHasExactSlot) {
                const aHasSingleTutor = aTags.includes("single_tutor")
                const bHasSingleTutor = bTags.includes("single_tutor")
                const aIsAdHoc = aTags.includes("ad_hoc")
                const bIsAdHoc = bTags.includes("ad_hoc")

                // Priority order:
                // 1. Single tutor, existing sessions only (not ad-hoc)
                // 2. Single tutor, ad-hoc (has fake sessions)
                // 3. Multi tutor groups

                // Case 1: A is single-tutor existing, B is anything else
                if (aHasSingleTutor && !aIsAdHoc) {
                    if (!bHasSingleTutor || bIsAdHoc) {
                        return -1 // A wins (existing single-tutor is best)
                    }
                }

                // Case 2: B is single-tutor existing, A is anything else
                if (bHasSingleTutor && !bIsAdHoc) {
                    if (!aHasSingleTutor || aIsAdHoc) {
                        return 1 // B wins (existing single-tutor is best)
                    }
                }

                // Case 3: Both are single-tutor ad-hoc - check for adjacent_to_existing
                if (
                    aHasSingleTutor &&
                    aIsAdHoc &&
                    bHasSingleTutor &&
                    bIsAdHoc
                ) {
                    const aHasAdjacent = aTags.includes("adjacent_to_existing")
                    const bHasAdjacent = bTags.includes("adjacent_to_existing")

                    if (aHasAdjacent && !bHasAdjacent) {
                        return -1 // A has adjacent sessions, B doesn't - A wins
                    }
                    if (!aHasAdjacent && bHasAdjacent) {
                        return 1 // B has adjacent sessions, A doesn't - B wins
                    }
                }

                // Case 4: A is single-tutor ad-hoc, B is multi-tutor
                if (aHasSingleTutor && aIsAdHoc && !bHasSingleTutor) {
                    return -1 // A wins (ad-hoc single-tutor beats multi-tutor)
                }

                // Case 5: B is single-tutor ad-hoc, A is multi-tutor
                if (bHasSingleTutor && bIsAdHoc && !aHasSingleTutor) {
                    return 1 // B wins (ad-hoc single-tutor beats multi-tutor)
                }

                // If we reach here, both have same single_tutor and ad-hoc status
                // Priority 5: good_class_match
                if (
                    aHasSingleTutor === bHasSingleTutor &&
                    aIsAdHoc === bIsAdHoc
                ) {
                    const aHasGoodClassMatch =
                        aTags.includes("good_class_match")
                    const bHasGoodClassMatch =
                        bTags.includes("good_class_match")

                    if (aHasGoodClassMatch && !bHasGoodClassMatch) {
                        return -1
                    }
                    if (!aHasGoodClassMatch && bHasGoodClassMatch) {
                        return 1
                    }
                }
            }
        }

        // Priority 5: If all tags are equivalent, sort by overall score descending
        return (b.total_score || 0) - (a.total_score || 0)
    })
}

export const processGroups = (groups) => {
    const uniqueGroups = removeDuplicateGroups(groups)
    return sortGroupsByPriority(uniqueGroups)
}

export const formatLastConnected = (lastConnected) => {
    if (!lastConnected) return "Jamais connecté"

    const accessDate = new Date(lastConnected)
    const now = new Date()
    const diffInMs = now - accessDate
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    const diffInDays = Math.floor(diffInHours / 24)

    if (diffInDays === 0) {
        if (diffInHours === 0) {
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
            return diffInMinutes <= 1
                ? "À l'instant"
                : `Il y a ${diffInMinutes} min`
        }
        return `Il y a ${diffInHours}h`
    } else if (diffInDays === 1) {
        return "Hier"
    } else if (diffInDays < 7) {
        return `Il y a ${diffInDays} jours`
    } else {
        return accessDate.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year:
                accessDate.getFullYear() !== now.getFullYear()
                    ? "numeric"
                    : undefined,
        })
    }
}

/**
 * Check if a tutor has both literary and scientific subjects
 * @param {Array} relevantSubjects - Array containing 'literary' and/or 'scientific'
 * @returns {boolean} - True if has both types
 */
export const hasBothSubjectTypes = (relevantSubjects = []) => {
    const hasLiterary = relevantSubjects.includes("literary")
    const hasScientific = relevantSubjects.includes("scientific")
    return hasLiterary && hasScientific
}
